import React, { useEffect, useRef, useState } from "react";
import { Button, Modal, ProgressBar } from "react-bootstrap";
import { toast } from "react-toastify";
import "./OrderSync.css";

export default function OrderSync({ scope, onSync, onStart }) {
  const [progress, setProgress] = useState(null);
  const [canceling, setCanceling] = useState(false);
  const taskRef = useRef(null);
  const busy = Boolean(progress);

  useEffect(() => {
    if (!busy) return undefined;
    const blockShortcuts = (event) => {
      if (!["Enter", "Escape", "ArrowLeft", "ArrowRight"].includes(event.key)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.key === "Enter") event.target.closest?.("button[data-sync-cancel]")?.click();
    };
    document.addEventListener("keydown", blockShortcuts, true);
    return () => document.removeEventListener("keydown", blockShortcuts, true);
  }, [busy]);

  useEffect(() => {
    setProgress(null);
    setCanceling(false);
    return () => {
      const task = taskRef.current;
      if (!task) return;
      taskRef.current = null;
      task.controller.abort();
      task.remove?.();
      if (task.desktop) task.desktop.cancelSync().catch(() => {});
    };
  }, [scope]);

  const start = async () => {
    if (taskRef.current) return;
    const task = { controller: new AbortController(), desktop: window.desktopPos };
    taskRef.current = task;
    setCanceling(false);
    setProgress({ percent: 0, detail: "Đang chuẩn bị đồng bộ…" });
    onStart?.();
    try {
      let result;
      if (task.desktop) {
        const update = async () => {
          try {
            const status = await task.desktop.status();
            if (taskRef.current === task && status.syncProgress) setProgress(status.syncProgress);
          } catch { /* The sync invocation reports errors to the user. */ }
        };
        task.remove = task.desktop.onChange(update);
        result = await task.desktop.sync({ refreshCatalog: true });
      } else {
        await onSync(task.controller.signal, (next) => {
          if (taskRef.current === task && !task.controller.signal.aborted) setProgress(next);
        });
      }
      if (taskRef.current !== task) return;
      if (task.controller.signal.aborted || result?.canceled) toast.info("Đã hủy đồng bộ.");
      else if (result?.pending || result?.review) toast.warning(`Đã tải dữ liệu. Còn ${result.pending || 0} bill chờ đồng bộ, ${result.review || 0} bill cần xử lý.`);
      else toast.success("Đồng bộ hoàn tất.");
    } catch (error) {
      if (taskRef.current === task) {
        if (task.controller.signal.aborted) toast.info("Đã hủy đồng bộ.");
        else toast.error(`Không thể đồng bộ: ${error.message}`);
      }
    } finally {
      task.remove?.();
      if (taskRef.current === task) {
        taskRef.current = null;
        setProgress(null);
        setCanceling(false);
      }
    }
  };

  const cancel = async () => {
    const task = taskRef.current;
    if (!task || task.controller.signal.aborted) return;
    setCanceling(true);
    if (task.desktop) {
      try {
        await task.desktop.cancelSync();
        task.controller.abort();
      } catch (error) {
        setCanceling(false);
        toast.error(`Không thể hủy đồng bộ: ${error.message}`);
      }
    } else task.controller.abort();
  };

  return <>
    <button type="button" className="dropdown-item-custom" onClick={start} disabled={Boolean(progress)}>
      <i className="fas fa-sync-alt" style={{ color: "#d4703e" }} />
      Đồng bộ
    </button>
    <Modal show={Boolean(progress)} centered backdrop="static" keyboard={false}
      className="order-sync-modal" backdropClassName="order-sync-backdrop" aria-labelledby="order-sync-title"
      onKeyDown={(event) => event.stopPropagation()}>
      <Modal.Header className="order-sync-header">
        <div className={`order-sync-icon${canceling ? " is-canceling" : ""}`} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 7v5h-5M4 17v-5h5" />
            <path d="M6.1 7a7 7 0 0 1 11.6-1L20 9M4 15l2.3 3A7 7 0 0 0 17.9 17" />
          </svg>
        </div>
        <div>
          <span className="order-sync-eyebrow">Đồng bộ dữ liệu</span>
          <Modal.Title id="order-sync-title">Đang đồng bộ</Modal.Title>
          <p className="order-sync-description">Cập nhật dữ liệu bán hàng mới nhất.</p>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="order-sync-progress-heading">
          <span>Tiến trình cập nhật</span>
          <strong>{progress?.percent || 0}%</strong>
        </div>
        <ProgressBar now={progress?.percent || 0} min={0} max={100} aria-label="Tiến trình đồng bộ" />
        <p className="order-sync-status" role="status" aria-live="polite">
          <span className={`order-sync-status-dot${canceling ? " is-canceling" : ""}`} aria-hidden="true" />
          {canceling ? "Đang hủy đồng bộ, chờ tác vụ hiện tại kết thúc…" : progress?.detail}
        </p>
      </Modal.Body>
      <Modal.Footer>
        <span className="order-sync-note">Dữ liệu đã lưu được giữ nguyên.</span>
        <Button variant="outline-danger" className="order-sync-cancel" onClick={cancel} disabled={canceling} data-sync-cancel>
          {canceling ? "Đang hủy…" : "Hủy đồng bộ"}
        </Button>
      </Modal.Footer>
    </Modal>
  </>;
}
